import { useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { customerMotionTransition } from '../customer-area'
import { motionTokens } from '../../../theme'
import { LockIcon } from './preferencesShared'

const STRIPE_TOOLTIP_DELAY_MS = 150

type StripeTrustBannerProps = {
  reducedMotion: boolean
  prefix: string
  brand: string
  suffix: string
  tooltip: string
}

export const StripeTrustBanner = ({
  reducedMotion,
  prefix,
  brand,
  suffix,
  tooltip,
}: StripeTrustBannerProps) => {
  const [stripeTooltipOpen, setStripeTooltipOpen] = useState(false)
  const stripeTooltipTimerRef = useRef<number | null>(null)

  useEffect(
    () => () => {
      if (stripeTooltipTimerRef.current !== null) {
        window.clearTimeout(stripeTooltipTimerRef.current)
      }
    },
    [],
  )

  const scheduleStripeTooltip = () => {
    if (stripeTooltipTimerRef.current !== null) {
      window.clearTimeout(stripeTooltipTimerRef.current)
    }
    stripeTooltipTimerRef.current = window.setTimeout(() => {
      setStripeTooltipOpen(true)
      stripeTooltipTimerRef.current = null
    }, STRIPE_TOOLTIP_DELAY_MS)
  }

  const hideStripeTooltip = () => {
    if (stripeTooltipTimerRef.current !== null) {
      window.clearTimeout(stripeTooltipTimerRef.current)
      stripeTooltipTimerRef.current = null
    }
    setStripeTooltipOpen(false)
  }

  return (
    <motion.div
      initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: motionTokens.durationNormalSeconds,
        ease: motionTokens.easingStandard,
      }}
      className="ca-theme-transition rounded-xl border border-(--color-border-default) border-l-[3px] border-l-(--color-status-success) bg-(--color-bg-surface-muted) px-3.5 py-2.5"
    >
      <div className="flex items-center gap-2 text-sm text-(--color-text-secondary)">
        <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-(--color-bg-card) text-(--color-text-muted)">
          <LockIcon className="h-3.5 w-3.5" />
        </span>
        <p className="min-w-0 leading-5">
          {prefix}{' '}
          <span className="relative inline-flex">
            <button
              type="button"
              onMouseEnter={scheduleStripeTooltip}
              onMouseLeave={hideStripeTooltip}
              onFocus={() => setStripeTooltipOpen(true)}
              onBlur={hideStripeTooltip}
              className="ca-theme-transition rounded-sm font-medium text-(--color-text-primary) underline decoration-(--color-border-strong) underline-offset-2 hover:text-(--color-text-secondary) focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--color-focus-ring)_40%,transparent)]"
              aria-label={brand}
            >
              {brand}
            </button>
            <AnimatePresence>
              {stripeTooltipOpen ? (
                <motion.span
                  initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 4 }}
                  transition={customerMotionTransition}
                  className="ca-theme-transition absolute top-full left-1/2 z-20 mt-2 w-72 -translate-x-1/2 rounded-lg border border-(--color-border-default) bg-(--color-tooltip-bg) px-3 py-2 text-left text-xs text-(--color-tooltip-text) shadow-(--shadow-md)"
                  role="tooltip"
                >
                  {tooltip}
                </motion.span>
              ) : null}
            </AnimatePresence>
          </span>{' '}
          {suffix}
        </p>
      </div>
    </motion.div>
  )
}
